/**
 * GhostSpriteDissolve — the death-beat body choreography machine (spec §1.1,
 * docs/plans/fx-grammar-specs/death-and-kill-beats.md).
 *
 * The real MobRenderer is destroyed the same frame the mob dies, so the body
 * can't animate its own death. Instead, this module respawns a GHOST sprite
 * from the victim's cached texture key (SpriteTextureSystem's cache survives
 * renderer destroy — the spec's §0.6 falsified-renderTexture finding) at the
 * DeathSnapshotStore-captured position, then drives one of a small set of
 * data-described dissolve programs via rAF (the substrate the spec ruled
 * available: setAlpha/setScale/setTint/setPosition/setAngle — no mask wipes,
 * no shader hooks).
 *
 * Programs are DATA ({ tint ramp, alpha curve, scale, rise/fall, rotate,
 * hold }) so all seven schools share this one machine (school tables live in
 * config/abilities/DeathVFX.ts). Ghosts are hard-capped (risk table: fast
 * respawn overlap) and cancellable by entity id (KillBeatDirector cancels on
 * rejoin). Texture-cache miss ⇒ returns false — the director falls back to a
 * flourish-only beat and records it in death_vfx meta.
 */

import type { ISprite, SpriteOptions } from '../../rendering/IRenderer';
import { createSprite } from '../../rendering/utils/Sprite';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import { gameLogger } from '../../../utils/GameLogger';
import type { DeathSnapshot } from '../../systems/combat/DeathSnapshotStore';

/**
 * A dissolve program — pure data, interpreted per-frame. All curves run over
 * `durationMs` minus the optional leading `holdMs` (the frost freeze-read).
 * Alpha always ramps 1→0 across the animated window (stepped when
 * `alphaFlickerSteps` is set — the arcane unstable phase-out).
 */
export interface DissolveProgram {
  /** Total ghost lifetime incl. hold. Clamped to GHOST_HARD_CAP_MS. */
  durationMs: number;
  /** Motionless lead-in (frost freeze) — sprite shows tintFrom, nothing moves. */
  holdMs?: number;
  /** Tint applied at spawn (and lerped toward tintTo when set). */
  tintFrom?: number;
  /** Tint ramp target — requires tintFrom as the lerp origin. */
  tintTo?: number;
  /** Discrete alpha bands instead of a smooth fade (arcane: 3). */
  alphaFlickerSteps?: number;
  /** Scale targets at t=1 (default 1 = no change). */
  scaleXTo?: number;
  scaleYTo?: number;
  /** World-y offset at t=1: negative rises (holy lift), positive drops. */
  dy?: number;
  /** Rotation at t=1, degrees; sign is flipped for left-facing ghosts so the
   *  physical fall reads "away from facing". */
  rotateDeg?: number;
}

/** Risk-table cap: a ghost must never outlive a fast respawn window. */
export const GHOST_HARD_CAP_MS = 1500;

export interface GhostSpriteDissolveDeps {
  createSprite: (opts: SpriteOptions) => ISprite;
  now: () => number;
}

interface ActiveGhost {
  sprite: ISprite;
  cancelled: boolean;
}

/** rAF when the environment has it (browser/jsdom), 16ms timeout otherwise —
 *  both advance under jest modern fake timers. */
function schedule(cb: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(cb);
  } else {
    setTimeout(cb, 16);
  }
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

function lerpColor(from: number, to: number, t: number): number {
  const r = Math.round(lerp((from >> 16) & 0xff, (to >> 16) & 0xff, t));
  const g = Math.round(lerp((from >> 8) & 0xff, (to >> 8) & 0xff, t));
  const b = Math.round(lerp(from & 0xff, to & 0xff, t));
  return (r << 16) | (g << 8) | b;
}

export class GhostSpriteDissolveSystem {
  private static instance: GhostSpriteDissolveSystem | null = null;

  private deps: GhostSpriteDissolveDeps;
  private ghosts = new Map<string, ActiveGhost>();

  /** Tests construct directly with a fake sprite factory; runtime uses getInstance(). */
  constructor(deps?: Partial<GhostSpriteDissolveDeps>) {
    this.deps = {
      createSprite: (opts) => createSprite(null, opts),
      now: () => Date.now(),
      ...deps,
    };
  }

  public static getInstance(): GhostSpriteDissolveSystem {
    if (!GhostSpriteDissolveSystem.instance) {
      GhostSpriteDissolveSystem.instance = new GhostSpriteDissolveSystem();
    }
    return GhostSpriteDissolveSystem.instance;
  }

  /**
   * Spawn + choreograph a ghost for `id` from the death snapshot. Returns
   * false (no ghost, caller plays flourish-only) when the snapshot carries no
   * texture key, the texture is not cached, or the sprite substrate is
   * unavailable (headless/tests). A live ghost for the same id is replaced.
   */
  public dissolve(id: string, snap: DeathSnapshot, program: DissolveProgram): boolean {
    if (!snap?.textureKey) return false;
    this.cancel(id);

    let sprite: ISprite;
    try {
      sprite = this.deps.createSprite({
        x: snap.x,
        y: snap.y,
        initialTextureKey: snap.textureKey,
        width: snap.width ?? 40,
        height: snap.height ?? 40,
        depth: Z_LAYERS.MOBS,
      });
    } catch (err) {
      gameLogger.debug('vfx', `[GhostSpriteDissolve] no ghost for ${id} (sprite substrate unavailable): ${err}`);
      return false;
    }
    // Cache-miss fallback (risk table): a ghost with no real texture would be
    // a blank rectangle — worse than no ghost.
    if (!sprite || (typeof sprite.hasTexture === 'function' && !sprite.hasTexture(snap.textureKey))) {
      try { sprite?.destroy(); } catch { /* non-critical */ }
      gameLogger.debug('vfx', `[GhostSpriteDissolve] texture ${snap.textureKey} not cached — flourish-only for ${id}`);
      return false;
    }

    const facingLeft = snap.facing === 'left';
    if (facingLeft) sprite.setFlipX(true);
    if (program.tintFrom !== undefined) sprite.setTint(program.tintFrom);

    const entry: ActiveGhost = { sprite, cancelled: false };
    this.ghosts.set(id, entry);

    const start = this.deps.now();
    const total = Math.min(Math.max(program.durationMs, 1), GHOST_HARD_CAP_MS);
    const hold = Math.min(program.holdMs ?? 0, total);
    const animMs = Math.max(total - hold, 1);
    const rotateSign = facingLeft ? -1 : 1;
    const baseX = snap.x;
    const baseY = snap.y;

    const tick = (): void => {
      if (entry.cancelled) return;
      const elapsed = this.deps.now() - start;
      if (elapsed >= total) {
        this.finish(id, entry);
        return;
      }
      if (elapsed > hold) {
        const t = Math.min((elapsed - hold) / animMs, 1);
        let alpha = 1 - t;
        if (program.alphaFlickerSteps && program.alphaFlickerSteps > 0) {
          // Stepped bands: 1 → 2/3 → 1/3 → (destroy) for 3 steps.
          alpha = Math.max(0, 1 - Math.floor(t * program.alphaFlickerSteps) / program.alphaFlickerSteps);
        }
        sprite.setAlpha(alpha);
        if (program.scaleXTo !== undefined || program.scaleYTo !== undefined) {
          sprite.setScale(lerp(1, program.scaleXTo ?? 1, t), lerp(1, program.scaleYTo ?? 1, t));
        }
        if (program.dy) {
          sprite.setPosition(baseX, baseY + program.dy * t);
        }
        if (program.rotateDeg) {
          sprite.setAngle(program.rotateDeg * rotateSign * t);
        }
        if (program.tintFrom !== undefined && program.tintTo !== undefined) {
          sprite.setTint(lerpColor(program.tintFrom, program.tintTo, t));
        }
      }
      schedule(tick);
    };
    schedule(tick);
    return true;
  }

  /** Kill a live ghost early (director calls this when the same id respawns). */
  public cancel(id: string): void {
    const entry = this.ghosts.get(id);
    if (!entry) return;
    entry.cancelled = true;
    try { entry.sprite.destroy(); } catch { /* non-critical */ }
    this.ghosts.delete(id);
  }

  /** Full teardown (disconnect / dispose). */
  public cancelAll(): void {
    for (const id of Array.from(this.ghosts.keys())) {
      this.cancel(id);
    }
  }

  /** Live ghost count — leak assertion surface for tests. */
  public activeCount(): number {
    return this.ghosts.size;
  }

  private finish(id: string, entry: ActiveGhost): void {
    entry.cancelled = true;
    try { entry.sprite.destroy(); } catch { /* non-critical */ }
    if (this.ghosts.get(id) === entry) this.ghosts.delete(id);
  }
}
